import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Boxes, Eye, EyeOff, KeyRound, Lock, LogIn, ScanLine, TrendingUp, User } from 'lucide-react';
import { ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { Botao } from '@/componentes/ui/botao';
import { CampoComIcone, Esqueleto } from '@/componentes/ui/primitivos';

/**
 * Entrada do sistema. É a primeira tela que um cliente vê numa demonstração,
 * então ela carrega a marca: painel de apresentação à esquerda (o que o
 * produto faz) e formulário limpo à direita. Em telas estreitas só o
 * formulário aparece — o coletor não precisa de vitrine.
 */
export function Entrar(): JSX.Element {
  const { usuario, carregando, entrar, entrando, erroLogin } = useSessao();
  const local = useLocation();
  const [login, definirLogin] = React.useState('');
  const [senha, definirSenha] = React.useState('');
  const [verSenha, definirVerSenha] = React.useState(false);

  if (carregando) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Esqueleto className="h-80 w-full max-w-sm rounded-2xl" />
      </div>
    );
  }
  if (usuario) {
    const destino = (local.state as { de?: string } | null)?.de ?? '/';
    return <Navigate to={destino} replace />;
  }

  const erro = erroLogin instanceof ErroHttp ? erroLogin : null;
  // Senha resetada no PBS (Mudar123): o servidor recusa com este código e a
  // tela vira a troca, no lugar do formulário.
  const trocandoSenha = erro?.codigo === 'trocar_senha';

  return (
    <div className="grid h-full lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] xl:grid-cols-[minmax(0,1.15fr)_minmax(0,520px)]">
      <Vitrine />

      <div className="flex items-center justify-center bg-superficie p-6 sm:p-10">
        <div className="w-full max-w-[380px]">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="gradiente-marca grid h-11 w-11 place-items-center rounded-xl text-lg font-black text-white shadow-marca">
              W
            </span>
            <span className="leading-tight">
              <span className="block text-[17px] font-bold tracking-[-0.02em]">WMS</span>
              <span className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-textoFraco">
                Megamamute
              </span>
            </span>
          </div>

          {trocandoSenha ? (
            <TrocaSenhaResetada login={login} />
          ) : (
          <>
          <h1 className="text-[26px] font-bold tracking-[-0.025em]">Entrar</h1>
          <p className="mt-1.5 text-[13.5px] text-textoFraco">
            Use seu usuário e senha do ERP. O acesso aos centros é o mesmo.
          </p>

          <form
            className="mt-7 space-y-4"
            onSubmit={(evento) => {
              evento.preventDefault();
              void entrar(login, senha);
            }}
          >
            <div>
              <label htmlFor="login" className="mb-1.5 block text-[12.5px] font-semibold">
                Login
              </label>
              <CampoComIcone
                id="login"
                icone={<User className="h-4 w-4" />}
                autoFocus
                autoComplete="username"
                value={login}
                onChange={(e) => definirLogin(e.target.value)}
                placeholder="seu usuário do ERP"
                required
              />
            </div>

            <div>
              <label htmlFor="senha" className="mb-1.5 block text-[12.5px] font-semibold">
                Senha
              </label>
              <CampoComIcone
                id="senha"
                icone={<Lock className="h-4 w-4" />}
                type={verSenha ? 'text' : 'password'}
                autoComplete="current-password"
                value={senha}
                onChange={(e) => definirSenha(e.target.value)}
                placeholder="••••••••"
                required
                sufixo={
                  <button
                    type="button"
                    onClick={() => definirVerSenha((v) => !v)}
                    aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                    className="grid h-8 w-8 place-items-center rounded-lg text-textoSuave transition-colors hover:bg-superficie2 hover:text-texto"
                  >
                    {verSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
              />
            </div>

            {erro ? (
              <p
                role="alert"
                className="rounded-xl border border-status-divergencia/30 bg-status-divergenciaFundo px-3.5 py-2.5 text-[13px] font-medium text-status-divergencia"
              >
                {erro.message}
              </p>
            ) : null}

            <Botao
              type="submit"
              variante="primaria"
              tamanho="grande"
              className="w-full"
              carregando={entrando}
              disabled={login.length === 0 || senha.length === 0}
            >
              {!entrando ? <LogIn className="h-[18px] w-[18px]" /> : null}
              {entrando ? 'Entrando…' : 'Entrar'}
            </Botao>
          </form>
          </>
          )}

          <p className="mt-8 text-center text-[11.5px] text-textoSuave">
            WMS Megamamute · acesso restrito a usuários autorizados
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * A janela do PBS para senha resetada: senha atual (Mudar123), senha nova e
 * confirmação, com caixa. Gravou, já entra; dali em diante só a nova vale.
 */
function TrocaSenhaResetada({ login }: { login: string }): JSX.Element {
  const { trocarSenha, trocandoSenha, erroTrocaSenha, limparErroLogin } = useSessao();
  const [senhaAtual, definirSenhaAtual] = React.useState('');
  const [senhaNova, definirSenhaNova] = React.useState('');
  const [confirmacao, definirConfirmacao] = React.useState('');
  const [verSenhas, definirVerSenhas] = React.useState(false);

  const erro = erroTrocaSenha instanceof ErroHttp ? erroTrocaSenha : null;
  const naoBate = confirmacao.length > 0 && confirmacao !== senhaNova;

  const botaoVer = (
    <button
      type="button"
      onClick={() => definirVerSenhas((v) => !v)}
      aria-label={verSenhas ? 'Ocultar senhas' : 'Mostrar senhas'}
      className="grid h-8 w-8 place-items-center rounded-lg text-textoSuave transition-colors hover:bg-superficie2 hover:text-texto"
    >
      {verSenhas ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <>
      <h1 className="text-[26px] font-bold tracking-[-0.025em]">Defina sua senha</h1>
      <p className="mt-1.5 text-[13.5px] text-textoFraco">
        A senha de <span className="font-semibold text-texto">{login}</span> foi resetada. Para
        entrar, digite a senha atual e escolha uma nova. Maiúsculas e minúsculas contam.
      </p>

      <form
        className="mt-7 space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          void trocarSenha({ login, senhaAtual, senhaNova, confirmacao }).catch(() => {
            /* o erro aparece pelo erroTrocaSenha */
          });
        }}
      >
        <div>
          <label htmlFor="senha-atual" className="mb-1.5 block text-[12.5px] font-semibold">
            Senha atual
          </label>
          <CampoComIcone
            id="senha-atual"
            icone={<Lock className="h-4 w-4" />}
            type={verSenhas ? 'text' : 'password'}
            autoFocus
            autoComplete="current-password"
            value={senhaAtual}
            onChange={(e) => definirSenhaAtual(e.target.value)}
            required
            sufixo={botaoVer}
          />
        </div>

        <div>
          <label htmlFor="senha-nova" className="mb-1.5 block text-[12.5px] font-semibold">
            Senha nova
          </label>
          <CampoComIcone
            id="senha-nova"
            icone={<KeyRound className="h-4 w-4" />}
            type={verSenhas ? 'text' : 'password'}
            autoComplete="new-password"
            maxLength={20}
            value={senhaNova}
            onChange={(e) => definirSenhaNova(e.target.value)}
            required
          />
        </div>

        <div>
          <label htmlFor="senha-confirmacao" className="mb-1.5 block text-[12.5px] font-semibold">
            Confirme a senha nova
          </label>
          <CampoComIcone
            id="senha-confirmacao"
            icone={<KeyRound className="h-4 w-4" />}
            type={verSenhas ? 'text' : 'password'}
            autoComplete="new-password"
            maxLength={20}
            value={confirmacao}
            onChange={(e) => definirConfirmacao(e.target.value)}
            required
          />
          {naoBate ? (
            <p className="mt-1.5 text-[12px] font-medium text-status-divergencia">
              A confirmação não bate com a senha nova.
            </p>
          ) : null}
        </div>

        {erro ? (
          <p
            role="alert"
            className="rounded-xl border border-status-divergencia/30 bg-status-divergenciaFundo px-3.5 py-2.5 text-[13px] font-medium text-status-divergencia"
          >
            {erro.message}
          </p>
        ) : null}

        <Botao
          type="submit"
          variante="primaria"
          tamanho="grande"
          className="w-full"
          carregando={trocandoSenha}
          disabled={
            senhaAtual.length === 0 || senhaNova.length === 0 || confirmacao !== senhaNova
          }
        >
          {!trocandoSenha ? <LogIn className="h-[18px] w-[18px]" /> : null}
          {trocandoSenha ? 'Salvando…' : 'Salvar e entrar'}
        </Botao>

        <button
          type="button"
          onClick={limparErroLogin}
          className="mx-auto flex items-center gap-1.5 text-[12.5px] font-medium text-textoFraco hover:text-texto"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Voltar
        </button>
      </form>
    </>
  );
}

function Vitrine(): JSX.Element {
  const destaques = [
    {
      icone: <Boxes className="h-[18px] w-[18px]" />,
      titulo: 'Mapa do CD em dois níveis',
      texto: 'Da planta das ruas ao rack porta-palete, com ocupação volumétrica por vaga.',
    },
    {
      icone: <ScanLine className="h-[18px] w-[18px]" />,
      titulo: 'Busca única',
      texto: 'SKU, descrição ou endereço no mesmo campo — o sistema reconhece o que você digitou.',
    },
    {
      icone: <TrendingUp className="h-[18px] w-[18px]" />,
      titulo: 'Divergências tratadas na hora',
      texto: 'Saldo negativo vira uma fila de trabalho com a origem já sugerida.',
    },
  ];

  /*
    ESTA TELA NÃO NOMEIA A PILHA.

    O rodapé dizia "Integrado ao ERP PBS · SQL Server", o parágrafo citava o
    razão do PBS e o formulário pedia "seu usuário do PBS". Login é a única
    página que responde sem sessão: quem abrisse a URL lia de graça qual ERP e
    qual banco atacar. Nada disso era informação para o operador — ele já sabe
    onde trabalha —, era para quem está do lado de fora.

    O formulário continua dizendo DE ONDE vem a credencial, que é o que ele
    precisa dizer; só troca o nome do produto por "ERP". Quem digita não perde
    nada, e quem varre a internet atrás de um alvo não ganha.

    Sem o rodapé sobram dois filhos, e `justify-between` jogaria o texto para o
    pé da coluna. O `my-auto` do bloco central o mantém onde estava, com a
    marca no topo — mesmo enquadramento, uma linha a menos.
  */
  return (
    <div className="gradiente-rail relative hidden flex-col overflow-hidden p-12 lg:flex">
      {/* Luz de marca + malha sutil: profundidade sem ruído. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 50% at 15% 10%, rgb(var(--cor-primaria) / 0.5), transparent 70%), radial-gradient(50% 45% at 85% 90%, rgb(var(--ac-violeta) / 0.32), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            'linear-gradient(rgb(255 255 255 / 0.5) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / 0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(80% 70% at 50% 40%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(80% 70% at 50% 40%, black, transparent)',
        }}
      />

      <div className="relative flex items-center gap-3">
        <span className="gradiente-marca grid h-11 w-11 place-items-center rounded-xl text-lg font-black text-white shadow-marca">
          W
        </span>
        <span className="leading-tight">
          <span className="block text-[18px] font-bold tracking-[-0.02em] text-white">WMS</span>
          <span className="block text-[10.5px] font-semibold uppercase tracking-[0.16em] text-rail-textoFraco">
            Megamamute
          </span>
        </span>
      </div>

      <div className="relative my-auto max-w-[460px]">
        <h2 className="text-[34px] font-bold leading-[1.12] tracking-[-0.03em] text-white">
          O seu centro de distribuição,{' '}
          <span className="bg-gradient-to-r from-indigo-300 to-violet-300 bg-clip-text text-transparent">
            visível inteiro
          </span>
          .
        </h2>
        <p className="mt-4 text-[14.5px] leading-relaxed text-rail-texto/70">
          Ocupação real por posição, calculada sobre o razão do ERP. Sem planilha paralela, sem
          recontagem, sem esperar o fechamento.
        </p>

        <ul className="mt-9 space-y-5">
          {destaques.map((d) => (
            <li key={d.titulo} className="flex gap-3.5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white ring-1 ring-inset ring-white/15">
                {d.icone}
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-white">{d.titulo}</span>
                <span className="block text-[12.5px] leading-relaxed text-rail-texto/60">
                  {d.texto}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

    </div>
  );
}
